// Phase 5 Migration C sub-track (b) — admin useVendors hook.
// Mirrors mobile useVendors; calls the vendors_with_totals_summary SECDEF RPC.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

export interface VendorWithTotals {
  id: string;
  business_id: string;
  company_name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  payment_terms: string | null;
  default_tax_type: string | null;
  status: string | null;
  total_spent: number;
  outstanding_bills: number;
  outstanding_amount: number;
  last_transaction_date: string | null;
}

interface RpcEnvelope {
  success: boolean;
  vendors?: VendorWithTotals[];
  error?: { code: string; message?: string };
}

export function useVendors(businessId: string | null | undefined) {
  const [vendors, setVendors] = useState<VendorWithTotals[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!businessId) {
        if (!cancelled) setLoading(false);
        return;
      }
      const { data, error: err } = await supabase.rpc('vendors_with_totals_summary');
      if (cancelled) return;
      if (err) {
        setError(err.message);
      } else {
        const env = data as RpcEnvelope | null;
        if (env && env.success && env.vendors) {
          setVendors(env.vendors);
          setError(null);
        } else if (env && !env.success) {
          setError(env.error?.message ?? env.error?.code ?? 'Failed to load vendors');
        }
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [businessId, reloadTick]);

  const refetch = useCallback(() => { setReloadTick((t) => t + 1); }, []);

  return { vendors, loading, error, refetch };
}
